<template>
  <svg
    ref="svgRef"
    :width="'100%'"
    :height="height"
    :viewBox="`0 0 ${measuredWidth} ${height}`"
    preserveAspectRatio="none"
  >
    <polyline
      :points="pointsAttr"
      :stroke="color"
      :stroke-width="strokeWidth"
      fill="none"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';

interface ZigZagProps {
  width?: number; // initial width fallback; actual width wird gemessen
  height?: number;
  color?: string;
  strokeWidth?: number;
  segmentLength?: number;
  amplitude?: number;
}

const props = withDefaults(defineProps<ZigZagProps>(), {
  width: 200,
  height: 8,
  color: 'currentColor',
  strokeWidth: 1,
  segmentLength: 20,
  amplitude: undefined,
});

const svgRef = ref<SVGSVGElement | null>(null);
const measuredWidth = ref<number>(props.width);

const effectiveAmplitude = computed(() => {
  const maxAmp = Math.max(0, props.height / 2 - props.strokeWidth / 2);
  const desired = props.amplitude ?? props.height / 2 - props.strokeWidth;
  return Math.min(Math.max(desired, 0), maxAmp);
});

const pointsAttr = computed(() => {
  const points: Array<[number, number]> = [];
  const centerY = props.height / 2;
  const amp = effectiveAmplitude.value;
  const halfStep = Math.max(1, props.segmentLength / 2);

  const width = measuredWidth.value;
  let x0 = 0;
  let y0 = centerY - amp; // start at top peak
  let dir = 1; // first move towards bottom peak

  points.push([x0, y0]);

  while (x0 < width) {
    const nextXIdeal = x0 + halfStep;
    const x1 = Math.min(nextXIdeal, width);
    const targetY = centerY + dir * amp;
    const progress = (x1 - x0) / halfStep;
    const y1 = y0 + (targetY - y0) * progress;
    points.push([x1, y1]);

    if (x1 < nextXIdeal) {
      break; // truncated segment; don't flip direction further
    }

    // prepare next segment
    x0 = x1;
    y0 = targetY;
    dir = -dir;
  }

  return points.map(([px, py]) => `${px},${py}`).join(' ');
});

let ro: ResizeObserver | null = null;

onMounted(() => {
  const el = svgRef.value;
  const update = () => {
    measuredWidth.value = el?.clientWidth || props.width;
  };
  update();
  if (typeof ResizeObserver !== 'undefined' && el) {
    ro = new ResizeObserver(update);
    ro.observe(el);
  } else {
    window.addEventListener('resize', update);
  }
});

onBeforeUnmount(() => {
  if (ro && svgRef.value) {
    ro.unobserve(svgRef.value);
    ro.disconnect();
  }
});
</script>

<style scoped></style>
